/**
 * web/src/components/shared/Input.tsx
 * Input matching `.input` (planning-docs/design_handoff_pixel_dashboard/
 * design_files/styles.css lines 490-525). 1px border-strong, white bg,
 * 13px font, 6×10 padding, 6px radius. Focus: indigo border + 3px
 * accent-soft ring. Readonly variant: muted bg + muted text.
 */
import { forwardRef } from 'react';
import type { InputHTMLAttributes } from 'react';
import { cn } from '../../lib/cn';

/** Public props for Input — inherits every native input attribute. */
export type InputProps = InputHTMLAttributes<HTMLInputElement>;

/**
 * Hand-rolled Input primitive.
 * Side effects: none — pure styled wrapper. ref forwards to the underlying
 * <input>; readOnly toggles the muted appearance per the handoff.
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, readOnly, ...props }, ref) => (
    <input
      ref={ref}
      readOnly={readOnly}
      className={cn(
        'w-full rounded-md border bg-bg px-2.5 py-1.5 text-[13px] text-text outline-none transition-colors duration-100',
        'border-border-strong placeholder:text-text-faint',
        'focus:border-accent focus:ring-[3px] focus:ring-accent-soft',
        readOnly && 'bg-bg-muted text-text-muted cursor-default',
        className,
      )}
      {...props}
    />
  ),
);
Input.displayName = 'Input';
